import { EntityViewField } from './entity.view';
import { MetadataEntityData } from '../../../metadata/entity/decorators/model/metadata.entity.data';
import { MetadataEntityFieldData } from '../../../metadata/entity/decorators/model/metadata.entity.field.data';
import { MetadataEntityFieldType } from '../../../metadata/entity/decorators/model/metadata.entity.field.types';
import { getMetadataEntityFieldType } from '../../../metadata/entity/decorators/utilities/get.entity.field.type';
import { resolveEmbeddedLevels } from '../../../metadata/entity/decorators/utilities/resolve.embedded.levels';
import * as Enumerable from 'linq';

/**
 * Builds legacy view-field collections from root fields and non-colliding, path-qualified filterable
 * fields from every embedded level.
 * @deprecated Use MetadataEntityViewFieldsDomain instead.
 */
export class EntityViewMetadata {

    public readonly highlightable: EntityViewField[];
    public readonly groupable: EntityViewField[];
    public readonly sortable: EntityViewField[];
    public readonly customFields: EntityViewField[];
    public readonly textSearchable: EntityViewField[];

    public readonly card: EntityViewField[];
    public readonly table: EntityViewField[];
    public readonly filters: EntityViewField[];
    public readonly systemFilters: EntityViewField[];

    public readonly fields: EntityViewField[];

    constructor(
        public readonly metadata: MetadataEntityData
    ) {

        const primaryFieldNames = new Set(metadata?.fields?.map(f => f.name) ?? []);
        const embeddedFilterableFields = collectEmbeddedFilterableFields(metadata, primaryFieldNames);
        const visibleFields = Enumerable
            .from([...metadata?.fields, ...embeddedFilterableFields]);
        //.where(x => !x.hidden);

        this.highlightable = visibleFields
            .where(x => {
                const type = getMetadataEntityFieldType(x);
                const isDate = type === MetadataEntityFieldType.Date || type === MetadataEntityFieldType.DateTime;
                const isHighlightable = x.highlightable;
                return isDate && isHighlightable;
            })
            .orderBy(x => x.displayName)
            .select(x => this.getViewField(x))
            .toArray();

        this.filters = visibleFields
            .where(x => x.filterable && x.type !== MetadataEntityFieldType.CustomField)
            .select(x => this.getViewField(x))
            .orderBy(x => x.name)
            .toArray();

        // Custom fields are excluded from the user-facing `filters` list but must be in the merge set:
        // getEffectiveEntityItemsRequest drops any request filter whose field is not listed here.
        this.systemFilters = visibleFields
            .where(x => (x.filterable || x.systemFilterable))
            .select(x => this.getViewField(x))
            .orderBy(x => x.name)
            .toArray();

        this.sortable = visibleFields
            .where(x => x.sortable)
            .select(x => this.getViewField(x))
            .orderBy(x => x.name)
            .toArray();

        this.groupable = visibleFields
            .where(x => x.groupable)
            .select(x => this.getViewField(x))
            .orderBy(x => x.name)
            .toArray();

        this.textSearchable = visibleFields
            .where(x => x.textSearchable)
            .select(x => this.getViewField(x))
            .orderBy(x => x.name)
            .toArray();

        this.customFields = visibleFields
            .where(x => x.type === MetadataEntityFieldType.CustomField)
            .select(x => this.getViewField(x))
            .orderBy(x => x.name)
            .toArray();

        this.card = this.table = visibleFields
            .where(x => x.type !== MetadataEntityFieldType.CustomField)
            .select(x => this.getViewField(x))
            .orderBy(x => x.name)
            .toArray();

        this.fields = visibleFields
            .select(x => this.getViewField(x))
            .orderBy(x => x.name)
            .toArray();

    }

    /** Returns root metadata only; path-qualified embedded fields exposed by collections are not resolved. */
    public getField(field: string | EntityViewField): MetadataEntityFieldData {

        const fieldName = typeof field === 'string'
            ? field
            : field._id;

        const metadata = this.metadata.fields.find(x => x.name === fieldName);

        return metadata;
    }

    private getViewField(field: MetadataEntityFieldData) {
        return {
            _id: field.name,
            path: field.path,
            name: field.displayName,
            attributeFilterName: field.attributeFilterName,
            systemHidden: field.hidden,
            visible: !field.hidden,
            moveable: true,
            sortOrder: null
        } as EntityViewField;
    }
}

/** Flatten filterable fields from the full secondaryFilter chain with absolute path prefixes. */
function collectEmbeddedFilterableFields(
    metadata: MetadataEntityData | null | undefined,
    primaryFieldNames: Set<string>,
): MetadataEntityFieldData[] {

    const result: MetadataEntityFieldData[] = [];
    for (const level of resolveEmbeddedLevels(metadata)) {
        for (const field of level.metadata.fields ?? []) {
            if (!field.filterable) continue;
            if (primaryFieldNames.has(field.name)) continue;
            result.push({ ...field, name: `${level.absolutePath}.${field.name}`, path: `${level.absolutePath}.${field.path ?? field.name}` });
        }
    }
    return result;
}
